import {
    type FC,
    type ReactNode,
    type ComponentProps,
    useRef,
    useCallback,
    useEffect,
} from 'react';
import { graphql, useFragment, type LoadMoreFn } from 'react-relay';
import { type OperationType } from 'relay-runtime';

import { QuickViewProvider } from 'dibs-search-product-tile/exports/QuickViewProvider';
import { ScrollCarousel } from 'dibs-scroll-carousel/exports/ScrollCarousel';
import { type FavoritesItemPropsType } from 'dibs-portfolio-folder/exports/FavoritesItemProvider';
import { CarouselItemTile } from '../CarouselItemTile/CarouselItemTile';

import { type ArrayElement } from 'dibs-ts-utils/exports/ArrayElement';
import { type ItemTilesCarousel_viewer$key } from './__generated__/ItemTilesCarousel_viewer.graphql';
import { type ItemTilesCarousel_user$key } from './__generated__/ItemTilesCarousel_user.graphql';
import {
    type ItemTilesCarousel_items$key,
    type ItemTilesCarousel_items$data,
} from './__generated__/ItemTilesCarousel_items.graphql';

type NullableItems = (ArrayElement<ItemTilesCarousel_items$data> | null)[];

const viewerFragment = graphql`
    fragment ItemTilesCarousel_viewer on Viewer
    @argumentDefinitions(
        fetchFavorites: { type: "Boolean", defaultValue: false }
        userId: { type: "String", defaultValue: "" }
    ) {
        ...CarouselItemTile_viewer @arguments(fetchFavorites: $fetchFavorites, userId: $userId)
    }
`;

const userFragment = graphql`
    fragment ItemTilesCarousel_user on User {
        ...CarouselItemTile_user
    }
`;

const itemsFragment = graphql`
    fragment ItemTilesCarousel_items on Item
    @relay(plural: true)
    @argumentDefinitions(
        fetchFavorites: { type: "Boolean", defaultValue: false }
        userId: { type: "String", defaultValue: "" }
        isTrade: { type: "Boolean", defaultValue: false }
        priceBookName: { type: "String" }
    ) {
        ...CarouselItemTile_item
            @arguments(
                fetchFavorites: $fetchFavorites
                userId: $userId
                isTrade: $isTrade
                priceBookName: $priceBookName
            )
    }
`;

export const ItemTilesCarousel: FC<{
    viewer: ItemTilesCarousel_viewer$key;
    user: ItemTilesCarousel_user$key | null | undefined;
    items: ItemTilesCarousel_items$key;
    currency: string;
    authModalLoader: $TSFixMe;
    itemsToShow: number;
    carouselCapacity: number;
    totalResults: number;
    loadNext?: LoadMoreFn<OperationType>;
    tileImageSize?: 'imageXSmall' | 'imageLarge' | 'imageMedium';
    isInfinite?: boolean;
    showArrows?: boolean;
    gapSize?: 'small' | 'medium' | 'large';
    showQuickView?: boolean;
    hideImagePadding?: boolean;
    trackingList?: string;
    fullWidth?: boolean;
    onArrowClick?: ComponentProps<typeof ScrollCarousel>['onArrowClick'];
    showFolder?: boolean;
    favoritesComponentOverrides?: ((props: FavoritesItemPropsType) => ReactNode)[];
    disableContentPadding?: boolean;
    dataTn?: string;
    tileClassName?: string;
}> = ({
    viewer: viewerRef,
    user: userRef,
    items: itemsRef,
    currency,
    authModalLoader,
    itemsToShow,
    carouselCapacity,
    totalResults,
    loadNext,
    tileImageSize,
    isInfinite,
    showArrows,
    gapSize = 'large',
    showQuickView = false,
    hideImagePadding = false,
    trackingList,
    fullWidth = false,
    onArrowClick,
    showFolder,
    favoritesComponentOverrides,
    disableContentPadding = false,
    dataTn,
    tileClassName,
}) => {
    const viewer = useFragment(viewerFragment, viewerRef);
    const user = useFragment(userFragment, userRef);
    const items = useFragment(itemsFragment, itemsRef);

    const remainingItems =
        carouselCapacity > totalResults
            ? totalResults - items.length
            : carouselCapacity - items.length;

    const fillerArr = remainingItems > 0 ? Array(remainingItems).fill(null) : [];
    const carouselItems: NullableItems = [...items, ...fillerArr].slice(0, carouselCapacity);

    const isLoadNextCalled = useRef(false);
    const trackedImpressionsRef = useRef<Set<string>>(new Set());

    useEffect(() => {
        //If the parrent query triggers refetch it will dispose loaded next items.
        //If items were loaded and disposed we need to load them again, to prevent infinitely showing shimmer.
        if (isLoadNextCalled.current && remainingItems && loadNext) {
            loadNext(remainingItems);
        }
    }, [remainingItems, loadNext]);

    const onSlideChange = useCallback(() => {
        if (!isLoadNextCalled.current && loadNext && remainingItems) {
            isLoadNextCalled.current = true;
            loadNext(remainingItems);
        }
    }, [loadNext, remainingItems]);

    if (carouselItems.length <= 0) {
        return null;
    }

    const carousel = (
        <QuickViewProvider>
            <ScrollCarousel
                animation="slide"
                enableScroll
                isInfinite={isInfinite}
                showArrows={showArrows}
                itemsToShow={itemsToShow}
                stepSize={Math.floor(itemsToShow)}
                onSlideChange={onSlideChange}
                gapSize={gapSize}
                scrollPadding={fullWidth ? 'small' : 'none'}
                onArrowClick={onArrowClick}
            >
                {carouselItems.map((item, index) => (
                    <CarouselItemTile
                        key={index}
                        viewer={viewer}
                        user={user}
                        item={item}
                        currency={currency}
                        authModalLoader={authModalLoader}
                        index={index}
                        lazyLoadStartIndex={itemsToShow - 1}
                        tileImageSize={tileImageSize}
                        showQuickView={showQuickView}
                        hideImagePadding={hideImagePadding}
                        trackingList={trackingList}
                        showFolder={showFolder}
                        favoritesComponentOverride={favoritesComponentOverrides?.[index]}
                        trackedImpressionsRef={trackedImpressionsRef}
                        disableContentPadding={disableContentPadding}
                        dataTn={dataTn}
                        tileClassName={tileClassName}
                    />
                ))}
            </ScrollCarousel>
        </QuickViewProvider>
    );

    if (fullWidth) {
        return <div className="primary-viewport-width">{carousel}</div>;
    }

    return carousel;
};
